import { useEffect, useRef, type ReactNode } from 'react';

/** The panel everything opens into.
 *
 *  Three iOS problems live here rather than in every screen that uses one.
 *
 *  Scroll behind it. `overflow: hidden` on the body does not stop touch
 *  scrolling on iOS — the page keeps moving under the sheet. Pinning the body
 *  does, but pins it to the top, so the scroll position has to be carried by
 *  hand and put back on close.
 *
 *  The keyboard. It shrinks the visual viewport and leaves the layout viewport
 *  alone, so a sheet anchored to the bottom of the layout ends up behind it. The
 *  `interactive-widget` viewport hint that fixes this elsewhere is not supported
 *  in any Safari, so the overlap is measured and handed to CSS.
 *
 *  Focus. It has to go into the sheet and come back out to whatever opened it,
 *  and it must not be able to wander into the page behind. */
export function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);

    const opener = document.activeElement as HTMLElement | null;
    const body = document.body;
    const y = window.scrollY;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.width = '100%';

    const vv = window.visualViewport;
    const sync = () => {
      if (!vv) return;
      // How much of the layout viewport the keyboard is sitting on top of.
      const hidden = window.innerHeight - vv.height - vv.offsetTop;
      document.documentElement.style.setProperty('--kb', `${Math.max(0, Math.round(hidden))}px`);
    };
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    sync();

    return () => {
      window.removeEventListener('keydown', onKey);
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      document.documentElement.style.removeProperty('--kb');
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.scrollTo(0, y);
      opener?.focus?.();
    };
  }, [onClose]);

  /** Keep Tab inside the sheet. Without this the next Tab lands on the list
   *  behind it, which is still there and still clickable. */
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Tab' || !ref.current) return;
    const focusable = ref.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet" role="dialog" aria-modal="true" ref={ref} onKeyDown={onKeyDown}>
        <div className="grab" />
        {children}
      </div>
    </div>
  );
}
